'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp,
  Users,
  PieChart,
  Globe,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Star,
  Check,
  ArrowRight,
  Shield,
  Smartphone,
  BarChart3,
  Lock,
  Headphones,
  Zap,
  BookOpen,
  Award,
  CreditCard,
  Clock,
  MessageCircle,
  Mail,
} from 'lucide-react';

/* ───────────────────────── DATA ───────────────────────── */

const tickers = [
  { symbol: 'EUR/USD', price: '1.0847', change: '+0.12%' },
  { symbol: 'GBP/USD', price: '1.2634', change: '+0.08%' },
  { symbol: 'USD/JPY', price: '149.82', change: '-0.15%' },
  { symbol: 'BTC/USD', price: '87,245', change: '+2.34%' },
  { symbol: 'ETH/USD', price: '3,421', change: '+1.87%' },
  { symbol: 'Gold', price: '2,987.50', change: '+0.45%' },
  { symbol: 'S&P 500', price: '5,842.3', change: '+0.32%' },
  { symbol: 'DAX', price: '18,472', change: '-0.21%' },
  { symbol: 'USD/CHF', price: '0.8823', change: '+0.05%' },
  { symbol: 'AUD/USD', price: '0.6543', change: '-0.18%' },
  { symbol: 'Crude Oil', price: '78.34', change: '+0.67%' },
  { symbol: 'SOL/USD', price: '142.80', change: '+3.12%' },
];

const instruments = [
  {
    symbol: 'Oil',
    code: 'CL',
    price: '98.00',
    buyers: '65%',
    path: 'M0,40 Q15,35 30,38 T60,30 T90,25 T120,32 T150,20',
    size: 'large',
  },
  {
    symbol: 'Brent Oil',
    code: '',
    price: '102.74',
    buyers: '71%',
    path: 'M0,35 Q20,40 40,30 T80,25 T120,20 T150,15',
    size: 'small',
  },
  {
    symbol: 'Nasdaq 100',
    code: '',
    price: '24593.91',
    buyers: '58%',
    path: 'M0,45 Q20,40 40,35 T80,20 T120,25 T150,10',
    size: 'small',
  },
  {
    symbol: 'Gold',
    code: 'XAU',
    price: '5045.22',
    buyers: '87%',
    path: 'M0,30 Q20,35 40,25 T80,20 T120,15 T150,22',
    size: 'large',
  },
  {
    symbol: 'Natural Gas',
    code: 'NG',
    price: '3.138',
    buyers: '82%',
    path: 'M0,40 Q20,30 40,35 T80,40 T120,20 T150,25',
    size: 'wide',
  },
  {
    symbol: 'Silver',
    code: '',
    price: '80.815',
    buyers: '73%',
    path: 'M0,35 Q20,30 40,28 T80,35 T120,20 T150,18',
    size: 'small',
  },
  {
    symbol: 'S&P 500',
    code: '',
    price: '6641.70',
    buyers: '62%',
    path: 'M0,40 Q20,35 40,30 T80,22 T120,18 T150,12',
    size: 'small',
  },
  {
    symbol: 'Germany 40',
    code: '',
    price: '23396.79',
    buyers: '55%',
    path: 'M0,30 Q20,38 40,25 T80,30 T120,28 T150,15',
    size: 'small',
  },
  {
    symbol: 'Bitcoin',
    code: '',
    price: '71123.65',
    buyers: '79%',
    path: 'M0,45 Q20,40 40,30 T80,35 T120,15 T150,10',
    size: 'small',
  },
];

const marketTabs = ['Most Popular', 'Shares', 'Commodities', 'Crypto', 'Indices', 'Forex'];

const stats = [
  { value: '10+', label: 'Years of Experience' },
  { value: '25K+', label: 'Satisfied Customers' },
  { value: '180+', label: 'Trading Instruments' },
  { value: '<12ms', label: 'Execution Speed' },
];

const regulations = [
  { name: 'EFSA', full: 'Estonian Financial Supervision Authority' },
  { name: 'CySEC', full: 'Cyprus Securities and Exchange Commission' },
  { name: 'CFTC', full: 'Commodity Futures Trading Commission' },
  { name: 'ASIC', full: 'Australian Securities & Investments Commission' },
  { name: 'FMA', full: 'Financial Markets Authority' },
  { name: 'FSCA', full: 'Financial Sector Conduct Authority' },
];

const awards = [
  { source: 'FXEmpire', year: '2025', title: 'Best Broker in EU' },
  { source: 'FXEmpire', year: '2025', title: 'Best Mobile Trading App' },
  { source: 'ForexBrokers', year: '2025', title: 'Best in Class Ease of Use' },
  { source: 'ADVFN', year: '2025', title: 'Best Provider Global Trading' },
  { source: 'ForexBrokers', year: '2024', title: 'Best in Class Trust Score' },
  { source: 'ADVFN', year: '2023', title: 'Best Provider Global Trading' },
];

const trustpilotReviews = [
  {
    title: 'Excellent platform',
    text: 'Very intuitive and fast. The execution is remarkable, spreads are tight. Would recommend to any serious trader.',
    author: 'Michael R.',
    time: '1 hour ago',
    stars: 5,
  },
  {
    title: 'Great app to use',
    text: 'Can see the profit just after buying the product online. The interface is clean and modern.',
    author: 'Sarah K.',
    time: '2 hours ago',
    stars: 5,
  },
  {
    title: 'Good platform',
    text: 'Good platform, a little bit more user friendly would be nice. Demo account very useful for beginners.',
    author: 'James T.',
    time: '2 hours ago',
    stars: 4,
  },
  {
    title: 'Good interface',
    text: 'Good interface especially for beginners. Easy to navigate and the charting tools are professional.',
    author: 'Anna M.',
    time: '5 hours ago',
    stars: 5,
  },
  {
    title: 'Fast execution',
    text: 'Trade execution is lightning fast. Never had slippage issues. Customer support responds quickly too.',
    author: 'David L.',
    time: '6 hours ago',
    stars: 5,
  },
];

const features = [
  {
    icon: 'TrendingUp',
    title: 'High Profit Potential',
    description:
      'Leverage market movements across multiple asset classes to maximize your portfolio returns with professional-grade tools.',
  },
  {
    icon: 'Users',
    title: 'Accessibility for All',
    description:
      "Whether you're a beginner or a seasoned professional, our platform adapts to your experience level with intuitive tools.",
  },
  {
    icon: 'PieChart',
    title: 'Portfolio Diversification',
    description:
      'Spread your investments across Forex, Stocks, Indices, Commodities and Crypto — all from a single account.',
  },
  {
    icon: 'Globe',
    title: 'Global Liquidity',
    description:
      'Access deep institutional liquidity pools ensuring tight spreads and fast execution across all market conditions.',
  },
];

const beginnerFeatures = [
  {
    icon: Clock,
    title: 'Get started in 3 steps',
    desc: 'Register your details, verify, and fund your account.',
  },
  {
    icon: BarChart3,
    title: 'Risk-free demo trading',
    desc: 'Practise with unlimited virtual funds and live market data.',
  },
  {
    icon: BookOpen,
    title: 'Trading Academy',
    desc: 'Learn the basics of trading or deepen your knowledge with ApexCFD.',
  },
];

const experiencedFeatures = [
  {
    icon: Zap,
    title: 'Indicators & Alerts',
    desc: "With over 100 technical indicators and flexible alerts you'll never miss a market opportunity.",
  },
  {
    icon: Award,
    title: 'Premium Service',
    desc: 'Enjoy exclusive services like periodic technical analysis, a dedicated Client Manager, and more.',
  },
  {
    icon: Shield,
    title: 'Professional Investors',
    desc: 'Eligible clients may request professional status, which allows access to different trading conditions.',
  },
];

const faqs = [
  {
    q: 'What is ApexCFD?',
    a: 'ApexCFD is a regulated online trading platform providing access to Forex, Stocks, Indices, Commodities and Crypto CFDs. We combine institutional-grade technology with competitive pricing and dedicated client support.',
  },
  {
    q: 'How do I open a trading account?',
    a: "Click 'Start Trading', complete the registration form with your personal details, verify your identity (KYC), and fund your account. The entire process takes less than 10 minutes and you can start trading immediately.",
  },
  {
    q: 'What is the minimum deposit?',
    a: 'Our Standard account requires a minimum deposit of $10,000. Higher-tier accounts offer progressively tighter spreads, dedicated support, and premium services.',
  },
  {
    q: 'Is my money safe with ApexCFD?',
    a: 'Yes. Client funds are held in segregated accounts at top-tier European banks, completely separate from company operational funds. We also offer negative balance protection and are regulated by financial authorities.',
  },
  {
    q: 'What trading platforms do you offer?',
    a: 'We offer our proprietary web-based platform, desktop applications for Windows and Mac, and fully-featured mobile apps for iOS and Android. All platforms include advanced charting, one-click trading, and real-time market data.',
  },
  {
    q: 'How can I withdraw my funds?',
    a: 'Withdrawals can be made via bank wire transfer, credit/debit card, or cryptocurrency. Requests are processed within 1-2 business days. There are no fees on standard withdrawals.',
  },
];

const footerLinks: Record<string, { label: string; href: string }[]> = {
  'About Us': [
    { label: 'About ApexCFD', href: '#' },
    { label: 'Regulation & Licences', href: '#' },
    { label: 'Security of Funds', href: '#' },
    { label: 'Careers', href: '#' },
  ],
  Trading: [
    { label: 'Forex', href: '#' },
    { label: 'Shares', href: '#' },
    { label: 'Indices', href: '#' },
    { label: 'Commodities', href: '#' },
    { label: 'Crypto', href: '#' },
  ],
  Resources: [
    { label: 'Trading Academy', href: '#' },
    { label: 'Economic Calendar', href: '#' },
    { label: 'Market Analysis', href: '#' },
    { label: 'FAQ', href: '#' },
  ],
  Legal: [
    { label: 'Terms & Conditions', href: '#' },
    { label: 'Privacy Policy', href: '#' },
    { label: 'Risk Disclosure', href: '#' },
    { label: 'Cookie Policy', href: '#' },
    { label: 'Client Money Protection', href: '#' },
  ],
};

const iconMap: Record<string, React.ElementType> = { TrendingUp, Users, PieChart, Globe };

/* ───────────────────────── PAGE ───────────────────────── */

export default function ApexPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const regRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const scrollReviews = (dir: number) => {
    if (reviewsRef.current) reviewsRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };
  const scrollReg = (dir: number) => {
    if (regRef.current) regRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' });
  };

  const navLinks = [
    { label: 'Markets', href: '#markets' },
    { label: 'Trading', href: '#trading' },
    { label: 'Company', href: '#company' },
    { label: 'Learn', href: '#learn' },
  ];

  return (
    <div className="min-h-screen bg-[#140A12] text-white">
      {/* ── Back to gallery ── */}
      <a
        href="/templates/"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 md:bottom-auto md:top-4 md:left-4 md:translate-x-0 z-[200] flex items-center gap-1.5 bg-black/70 hover:bg-black/90 backdrop-blur-sm text-white text-xs font-semibold px-4 py-2 rounded-full transition-all duration-200 shadow-xl border border-white/10"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        <span className="hidden sm:inline">All Templates</span>
        <span className="sm:hidden">Templates</span>
      </a>

      {/* ── RISK DISCLAIMER BAR ── */}
      <div className="bg-[#0F060B] text-[11px] text-[#AB8896] py-2 px-4 text-center leading-relaxed border-b border-white/5">
        CFDs are complex instruments and come with a high risk of losing money rapidly due to
        leverage.{' '}
        <strong className="text-[#D0A0B0]">
          80% of retail investor accounts lose money when trading CFDs with this provider.
        </strong>{' '}
        You should consider whether you can afford to take the high risk of losing your money.
      </div>

      {/* ── HEADER ── */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#140A12]/95 backdrop-blur-md shadow-lg shadow-black/20' : 'bg-[#140A12]'
        } border-b border-white/5`}
      >
        <div className="site-container flex items-center justify-between h-16">
          <Link href="/templates/apex" className="flex items-center gap-1.5">
            <span className="text-2xl font-bold text-white">
              Apex<span className="text-[#E11D48]">CFD</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-sm font-medium text-[#AB8896] hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link href="#" className="text-sm font-medium text-[#AB8896] hover:text-white">
              Log In
            </Link>
            <Link
              href="#"
              className="bg-[#E11D48] hover:bg-[#BE123C] text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors"
            >
              Start trading
            </Link>
          </div>

          <button className="md:hidden p-2 text-white" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden bg-[#1A0D16] border-t border-white/5 overflow-hidden"
            >
              <div className="site-container py-4 flex flex-col gap-3">
                {navLinks.map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    className="text-sm text-[#AB8896] py-2"
                    onClick={() => setMobileOpen(false)}
                  >
                    {l.label}
                  </a>
                ))}
                <Link
                  href="#"
                  className="bg-[#E11D48] text-white text-sm font-semibold px-6 py-2.5 rounded-full text-center mt-2"
                >
                  Start trading
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── HERO ── */}
      <section className="relative pt-16 pb-24 overflow-hidden">
        {/* Red glow streaks */}
        <div className="absolute top-20 right-[20%] w-[500px] h-[300px] bg-[#E11D48]/8 rounded-full blur-[100px] rotate-[-20deg]" />
        <div className="absolute top-40 right-[10%] w-[400px] h-[200px] bg-[#9F1239]/10 rounded-full blur-[80px] rotate-[-30deg]" />

        <div className="site-container relative z-10 grid md:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold leading-tight mb-6">
              Markets move fast.
              <br />
              <span className="text-[#E11D48]">So can you.</span>
            </h1>
            <p className="text-[#AB8896] text-lg mb-8 max-w-md">
              Trade global markets with our award-winning trading platform.
            </p>
            <div className="flex flex-wrap gap-4 mb-12">
              <Link
                href="#"
                className="bg-[#E11D48] hover:bg-[#BE123C] text-white font-semibold px-8 py-3.5 rounded-full transition-colors"
              >
                Start trading
              </Link>
              <Link
                href="#"
                className="border border-white/30 text-white hover:bg-white/5 font-semibold px-8 py-3.5 rounded-full transition-colors"
              >
                Try free demo
              </Link>
            </div>
            {/* Trust bar */}
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div>
                <p className="text-2xl font-bold text-white">
                  33+<span className="text-base font-normal text-[#AB8896] ml-1">Million</span>
                </p>
                <p className="text-[11px] text-[#8D6B7B]">
                  Registered customers
                  <br />
                  since inception
                </p>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-[#00B67A] text-[#00B67A]" />
                  ))}
                  <div className="relative w-4 h-4">
                    <Star className="w-4 h-4 text-[#00B67A] absolute" />
                    <div className="absolute inset-0 overflow-hidden" style={{ width: '40%' }}>
                      <Star className="w-4 h-4 fill-[#00B67A] text-[#00B67A]" />
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-bold text-white">4.2</p>
                  <p className="text-[10px] text-[#8D6B7B]">Trustpilot rating</p>
                </div>
              </div>
              <div className="w-px h-10 bg-white/10" />
              <div>
                <p className="text-xs text-[#8D6B7B]">
                  Listed since <strong className="text-[#AB8896]">2013</strong>
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right — 3D devices */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="flex justify-center relative"
          >
            {/* Monitor frame in 3D perspective */}
            <div className="relative" style={{ perspective: '1200px' }}>
              <div
                className="w-[420px] h-[280px] bg-[#1F0D18] rounded-xl border border-white/10 shadow-2xl shadow-[#E11D48]/10 overflow-hidden"
                style={{ transform: 'rotateY(-12deg) rotateX(4deg)' }}
              >
                {/* Monitor top bar */}
                <div className="bg-[#180B14] px-4 py-2 flex items-center gap-2 border-b border-white/5">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-[#10B981]/60" />
                  </div>
                  <span className="text-[10px] text-[#8D6B7B] ml-2">ApexCFD — Web Platform</span>
                </div>
                {/* Monitor content */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-white text-sm font-bold">EUR/USD</p>
                      <p className="text-[#10B981] text-xs">+0.12%</p>
                    </div>
                    <div className="flex gap-2">
                      <span className="bg-[#10B981]/20 text-[#10B981] text-[10px] px-3 py-1 rounded font-semibold">
                        BUY 1.0848
                      </span>
                      <span className="bg-[#EF4444]/20 text-[#EF4444] text-[10px] px-3 py-1 rounded font-semibold">
                        SELL 1.0846
                      </span>
                    </div>
                  </div>
                  <svg viewBox="0 0 380 140" className="w-full">
                    <defs>
                      <linearGradient id="apexMonGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#E11D48" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#E11D48" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M0,100 Q30,95 60,85 T120,70 T180,55 T240,65 T300,40 T360,25 L380,20"
                      fill="none"
                      stroke="#E11D48"
                      strokeWidth="2"
                    />
                    <path
                      d="M0,100 Q30,95 60,85 T120,70 T180,55 T240,65 T300,40 T360,25 L380,20 L380,140 L0,140 Z"
                      fill="url(#apexMonGrad)"
                    />
                    {/* Grid lines */}
                    {[35, 70, 105].map((y) => (
                      <line
                        key={y}
                        x1="0"
                        y1={y}
                        x2="380"
                        y2={y}
                        stroke="white"
                        strokeOpacity="0.03"
                      />
                    ))}
                  </svg>
                  {/* Bottom mini row */}
                  <div className="flex gap-3 mt-2">
                    {[
                      { s: 'GBP/USD', c: '+0.08%' },
                      { s: 'Gold', c: '+0.45%' },
                      { s: 'BTC/USD', c: '+2.34%' },
                      { s: 'S&P 500', c: '+0.32%' },
                    ].map((t) => (
                      <div key={t.s} className="text-[9px] text-[#8D6B7B]">
                        <span className="text-white/60">{t.s}</span>{' '}
                        <span className="text-[#10B981]">{t.c}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Smartphone overlaid */}
              <div
                className="absolute -bottom-8 -right-4 w-[140px] h-[280px] bg-[#1F0D18] rounded-[24px] border-2 border-white/10 shadow-2xl shadow-black/40 overflow-hidden z-10"
                style={{ transform: 'rotateY(-8deg)' }}
              >
                <div className="bg-[#180B14] px-3 py-2 flex items-center justify-between border-b border-white/5">
                  <span className="text-[8px] text-white font-bold">ApexCFD</span>
                  <span className="text-[7px] text-[#E11D48]">LIVE</span>
                </div>
                <div className="p-2.5">
                  <p className="text-white text-[10px] font-semibold">EUR/USD</p>
                  <p className="text-white text-lg font-bold">1.0847</p>
                  <svg viewBox="0 0 110 60" className="w-full my-2">
                    <defs>
                      <linearGradient id="apexPhoneGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#E11D48" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#E11D48" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M0,45 Q15,40 30,35 T60,25 T90,15 T110,10"
                      fill="none"
                      stroke="#E11D48"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M0,45 Q15,40 30,35 T60,25 T90,15 T110,10 L110,60 L0,60 Z"
                      fill="url(#apexPhoneGrad)"
                    />
                  </svg>
                  {[
                    { s: 'Gold', p: '5,045' },
                    { s: 'BTC', p: '87,245' },
                  ].map((t) => (
                    <div
                      key={t.s}
                      className="flex justify-between text-[8px] py-1.5 border-t border-white/5"
                    >
                      <span className="text-[#AB8896]">{t.s}</span>
                      <span className="text-white">{t.p}</span>
                    </div>
                  ))}
                </div>
                <div className="px-2.5 pb-3 grid grid-cols-2 gap-1.5 mt-auto">
                  <button className="bg-[#10B981] text-white text-[8px] font-bold py-2 rounded-lg">
                    BUY
                  </button>
                  <button className="bg-[#EF4444] text-white text-[8px] font-bold py-2 rounded-lg">
                    SELL
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── BENTO INSTRUMENTS GRID ── */}
      <section className="site-section pb-12" id="markets">
        <div className="site-container">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Trade intuitively on thousands of CFDs
          </h2>
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {marketTabs.map((tab, i) => (
              <button
                key={tab}
                onClick={() => setActiveTab(i)}
                className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                  activeTab === i
                    ? 'bg-[#E11D48] text-white border-[#E11D48]'
                    : 'border-[#3D1A2E] text-[#AB8896] hover:border-[#E11D48]/50'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          {/* Bento grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {instruments.map((inst, i) => {
              const isLarge = inst.size === 'large';
              const isWide = inst.size === 'wide';
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  className={`bg-[#1F0D18] rounded-2xl border border-white/5 p-5 hover:border-[#E11D48]/30 transition-colors relative overflow-hidden ${
                    isLarge ? 'row-span-2' : ''
                  } ${isWide ? 'col-span-2' : ''}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-full bg-[#E11D48]/20 flex items-center justify-center">
                      <TrendingUp className="w-3 h-3 text-[#E11D48]" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-semibold">{inst.symbol}</p>
                      {inst.code && <p className="text-[#8D6B7B] text-[10px]">{inst.code}</p>}
                    </div>
                  </div>
                  <p className="text-white text-xl font-bold mt-2">{inst.price}</p>
                  <p className="text-[#AB8896] text-xs mt-0.5">{inst.buyers} are buyers now</p>
                  <svg
                    viewBox="0 0 150 50"
                    className={`w-full ${isLarge ? 'mt-6 h-24' : 'mt-3'}`}
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <linearGradient id={`apexGrad${i}`} x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#E11D48" stopOpacity="0.15" />
                        <stop offset="100%" stopColor="#E11D48" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d={inst.path} fill="none" stroke="#E11D48" strokeWidth="1.5" />
                    <path d={`${inst.path} L150,50 L0,50 Z`} fill={`url(#apexGrad${i})`} />
                  </svg>
                </motion.div>
              );
            })}
          </div>
          <a
            href="#"
            className="inline-flex items-center gap-1 text-sm text-[#AB8896] hover:text-white mt-6 transition-colors"
          >
            Explore all markets <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* ── GLOBALLY TRUSTED ── */}
      <section className="site-section" id="company">
        <div className="site-container">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">A globally trusted CFD provider</h2>
          <div className="flex gap-12 mb-12">
            <div>
              <p className="text-5xl md:text-6xl font-bold text-white">
                65<span className="text-xl font-normal text-[#AB8896] ml-1">Countries</span>
              </p>
            </div>
            <div>
              <p className="text-5xl md:text-6xl font-bold text-white">
                15+<span className="text-xl font-normal text-[#AB8896] ml-1">Licenses</span>
              </p>
            </div>
          </div>

          {/* Regulation slider */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#AB8896] mb-4">Global Fintech Group</h3>
            <div className="relative">
              <div
                ref={regRef}
                className="flex gap-3 overflow-x-auto scrollbar-hide pb-2"
                style={{ scrollbarWidth: 'none' }}
              >
                {regulations.map((r, i) => (
                  <div
                    key={i}
                    className="flex-shrink-0 w-[180px] bg-[#1F0D18] border border-white/5 rounded-xl p-4 text-center hover:border-[#E11D48]/20 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#E11D48]/10 mx-auto mb-3 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-[#E11D48]" />
                    </div>
                    <p className="text-white font-bold text-sm">{r.name}</p>
                    <p className="text-[#8D6B7B] text-[10px] mt-1 leading-tight">{r.full}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3 justify-end">
                <button
                  onClick={() => scrollReg(-1)}
                  className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-[#AB8896] hover:text-white hover:border-white/20"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => scrollReg(1)}
                  className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-[#AB8896] hover:text-white hover:border-white/20"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Awards */}
          <div className="mt-14">
            <h3 className="text-lg font-semibold text-[#AB8896] mb-6">Top Industry Awards</h3>
            <div className="flex flex-wrap gap-6">
              {awards.map((aw, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Award className="w-5 h-5 text-[#F59E0B]" />
                  <div>
                    <p className="text-xs text-[#8D6B7B]">{aw.source}</p>
                    <p className="text-2xl font-bold text-white">{aw.year}</p>
                    <p className="text-xs text-[#AB8896] font-medium">{aw.title}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link
              href="#"
              className="inline-block mt-8 bg-[#E11D48] hover:bg-[#BE123C] text-white font-semibold px-6 py-3 rounded-full transition-colors"
            >
              Join ApexCFD
            </Link>
          </div>
        </div>
      </section>

      {/* ── TRUSTPILOT REVIEWS ── */}
      <section className="site-section">
        <div className="site-container">
          <div className="flex items-center gap-4 mb-2">
            <Star className="w-8 h-8 fill-[#00B67A] text-[#00B67A]" />
            <div>
              <p className="text-xl font-bold text-white">Trustpilot</p>
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-[#00B67A] text-[#00B67A]" />
                  ))}
                  <Star className="w-4 h-4 text-[#00B67A]" />
                </div>
                <span className="text-sm text-[#AB8896]">4.2 | Great</span>
                <span className="text-xs text-[#8D6B7B]">18,491 reviews</span>
              </div>
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mt-6 mb-8">Our traders say it best</h2>

          <div className="relative">
            <div
              ref={reviewsRef}
              className="flex gap-4 overflow-x-auto pb-4"
              style={{ scrollbarWidth: 'none' }}
            >
              {trustpilotReviews.map((r, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 w-[300px] bg-[#1F0D18] border border-white/5 rounded-xl p-6 hover:border-[#E11D48]/20 transition-colors"
                >
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: r.stars }).map((_, si) => (
                      <div
                        key={si}
                        className="w-6 h-6 bg-[#00B67A] flex items-center justify-center"
                      >
                        <Star className="w-3.5 h-3.5 fill-white text-white" />
                      </div>
                    ))}
                    {r.stars < 5 &&
                      Array.from({ length: 5 - r.stars }).map((_, si) => (
                        <div
                          key={si}
                          className="w-6 h-6 bg-[#2B1A24] flex items-center justify-center"
                        >
                          <Star className="w-3.5 h-3.5 text-[#684A58]" />
                        </div>
                      ))}
                  </div>
                  <h4 className="text-white font-semibold text-sm mb-2">{r.title}</h4>
                  <p className="text-[#AB8896] text-sm leading-relaxed mb-4">{r.text}</p>
                  <p className="text-[#8D6B7B] text-xs">
                    {r.author}, {r.time}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3 justify-end">
              <button
                onClick={() => scrollReviews(-1)}
                className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-[#AB8896] hover:text-white hover:border-white/20"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => scrollReviews(1)}
                className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-[#AB8896] hover:text-white hover:border-white/20"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <p className="text-xs text-[#8D6B7B] mt-4">
            Showing our 4 &amp; 5 star reviews in your language
          </p>
        </div>
      </section>

      {/* ── APP SECTION ── */}
      <section className="site-section" id="trading">
        <div className="site-container">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Phone mockup */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <div className="w-[260px] h-[520px] bg-[#1F0D18] rounded-[40px] border-[3px] border-[#3D1A2E] p-3 shadow-2xl shadow-[#E11D48]/5">
                <div className="w-full h-full bg-[#180B14] rounded-[32px] overflow-hidden flex flex-col">
                  <div className="bg-[#1F0D18] px-4 py-3 border-b border-white/5">
                    <div className="flex justify-between items-center">
                      <span className="text-white text-xs font-bold">Sell</span>
                      <ChevronDown className="w-3 h-3 text-[#AB8896]" />
                    </div>
                  </div>
                  <div className="flex-1 p-4">
                    <p className="text-white text-3xl font-bold mb-1">3,356.44</p>
                    <p className="text-[#10B981] text-xs mb-4">+1.23%</p>
                    <svg viewBox="0 0 200 100" className="w-full mb-4">
                      <path
                        d="M0,80 Q25,70 50,60 T100,40 T150,50 T200,20"
                        fill="none"
                        stroke="#E11D48"
                        strokeWidth="2"
                      />
                    </svg>
                    <div className="space-y-3">
                      <p className="text-[#AB8896] text-xs font-medium">Set Limits</p>
                      {['Close at profit', 'Close at loss', 'Trailing stop'].map((label) => (
                        <div key={label} className="flex justify-between items-center">
                          <span className="text-white text-xs">{label}</span>
                          <div className="w-8 h-4 bg-[#3D1A2E] rounded-full" />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-4 pt-0">
                    <button className="w-full bg-[#E11D48] text-white font-bold py-3 rounded-xl text-sm">
                      Sell
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Powerful trading.
                <br />
                Seamless app.
              </h2>
              <p className="text-[#AB8896] text-lg leading-relaxed mb-8">
                <strong className="text-white">Limit your exposure.</strong> Set Take Profit and
                Stop Loss to help protect your gains and limit losses.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  'Practise risk-free',
                  'Buy or Sell',
                  'Manage your risk',
                  'Get technical',
                  'Discover trends',
                ].map((tag) => (
                  <span
                    key={tag}
                    className="text-sm border border-white/20 text-[#AB8896] px-4 py-2 rounded-full hover:border-[#E11D48]/50 hover:text-white transition-colors cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              {/* App store badges */}
              <div className="flex gap-3">
                <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-white" />
                  <div>
                    <p className="text-[8px] text-[#AB8896]">Download on the</p>
                    <p className="text-xs font-semibold text-white">App Store</p>
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-white" />
                  <div>
                    <p className="text-[8px] text-[#AB8896]">GET IT ON</p>
                    <p className="text-xs font-semibold text-white">Google Play</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FUNDS / PAYMENTS ── */}
      <section className="site-section">
        <div className="site-container">
          <h2 className="text-3xl md:text-4xl font-bold mb-10">Take control of your funds</h2>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              {
                icon: CreditCard,
                title: 'Straightforward charges',
                desc: '0% opening / closing trades fees. 0% deposit and withdrawal fees.',
              },
              {
                icon: Zap,
                title: 'Fast withdrawals processing',
                desc: 'Get your funds quickly with our streamlined withdrawal process.',
              },
              {
                icon: Globe,
                title: 'Multiple global payment methods',
                desc: 'Choose from various payment options that suit your needs.',
              },
            ].map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-b from-[#1F0D18] to-[#140A12] rounded-xl border border-white/5 p-6"
              >
                <f.icon className="w-8 h-8 text-[#E11D48] mb-4" />
                <h3 className="text-white font-semibold mb-2">{f.title}</h3>
                <p className="text-[#AB8896] text-sm">{f.desc}</p>
              </motion.div>
            ))}
          </div>
          {/* Payment logos (text-based) */}
          <div className="flex flex-wrap items-center justify-center gap-8 py-6 border-t border-b border-white/5">
            {['VISA', 'mastercard', 'Skrill', 'Apple Pay', 'G Pay', 'PayPal'].map((p) => (
              <span key={p} className="text-[#8D6B7B] text-sm font-semibold tracking-wider">
                {p}
              </span>
            ))}
          </div>
          <div className="mt-8">
            <Link
              href="#"
              className="bg-[#E11D48] hover:bg-[#BE123C] text-white font-semibold px-6 py-3 rounded-full transition-colors"
            >
              Open account
            </Link>
          </div>
        </div>
      </section>

      {/* ── BEGINNER / EXPERIENCED ── */}
      <section className="site-section" id="learn">
        <div className="site-container space-y-16">
          {/* Beginner */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-8">Beginner trader?</h2>
            <div className="bg-gradient-to-r from-[#1F0D18] to-[#2B0D1F] rounded-2xl border border-white/5 p-8">
              <div className="grid md:grid-cols-3 gap-8">
                {beginnerFeatures.map((f, i) => (
                  <div key={i} className="flex flex-col items-start gap-3">
                    <f.icon className="w-8 h-8 text-[#E11D48]" strokeWidth={1.5} />
                    <h3 className="text-white font-semibold">{f.title}</h3>
                    <p className="text-[#AB8896] text-sm">{f.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <Link
              href="#"
              className="inline-block mt-6 border border-white/20 text-white font-semibold px-6 py-3 rounded-full hover:bg-white/5 transition-colors"
            >
              Try free demo
            </Link>
          </div>

          {/* Experienced */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-8">Experienced trader?</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {experiencedFeatures.map((f, i) => (
                <div
                  key={i}
                  className="bg-[#1F0D18] rounded-2xl border border-white/5 p-8 hover:border-[#E11D48]/20 transition-colors"
                >
                  <f.icon className="w-8 h-8 text-[#E11D48] mb-4" strokeWidth={1.5} />
                  <h3 className="text-white font-semibold mb-2">{f.title}</h3>
                  <p className="text-[#AB8896] text-sm">{f.desc}</p>
                </div>
              ))}
            </div>
            <Link
              href="#"
              className="inline-block mt-6 bg-[#E11D48] hover:bg-[#BE123C] text-white font-semibold px-6 py-3 rounded-full transition-colors"
            >
              Start trading
            </Link>
          </div>
        </div>
      </section>

      {/* ── SUPPORT ── */}
      <section className="site-section">
        <div className="site-container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Chat mockup */}
            <div className="flex justify-center">
              <div className="w-[300px] bg-[#1F0D18] rounded-2xl border border-white/10 p-6 space-y-4">
                <div className="flex justify-end">
                  <div className="bg-[#E11D48] text-white text-sm px-4 py-3 rounded-2xl rounded-br-md max-w-[200px]">
                    Hi, can you help me?
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-[#3D1A2E] text-white text-sm px-4 py-3 rounded-2xl rounded-bl-md max-w-[220px]">
                    Sure! How can I assist you today?
                  </div>
                </div>
                <div className="flex gap-1 pt-2">
                  {['😊', '👍', '🎉', '❤️'].map((e) => (
                    <span
                      key={e}
                      className="w-8 h-8 bg-white/5 rounded-full flex items-center justify-center text-sm"
                    >
                      {e}
                    </span>
                  ))}
                  <span className="w-8 h-8 bg-white/5 rounded-full flex items-center justify-center text-[10px] text-[#AB8896]">
                    +10
                  </span>
                </div>
              </div>
            </div>
            {/* Text */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">We&apos;re here for you</h2>
              <p className="text-[#AB8896] text-lg mb-6">
                Professional customer service by human agents, whenever you need it.
              </p>
              <div className="flex gap-3">
                <Link
                  href="#"
                  className="flex items-center gap-2 border border-white/20 text-white px-5 py-2.5 rounded-full hover:bg-white/5 transition-colors text-sm font-medium"
                >
                  <MessageCircle className="w-4 h-4" /> Chat
                </Link>
                <Link
                  href="#"
                  className="flex items-center gap-2 text-[#AB8896] hover:text-white transition-colors"
                >
                  <Mail className="w-5 h-5" />
                </Link>
              </div>
              <a
                href="#"
                className="inline-flex items-center gap-1 text-sm text-[#AB8896] hover:text-white mt-4 transition-colors"
              >
                Visit our FAQs <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="site-section">
        <div className="site-container">
          <h2 className="text-3xl font-bold mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl space-y-3">
            {faqs.map((f, i) => (
              <div
                key={i}
                className="bg-[#1F0D18] rounded-xl border border-white/5 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-semibold text-white text-sm">{f.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-[#E11D48] flex-shrink-0 transition-transform duration-300 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-sm text-[#AB8896] leading-relaxed">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#0F060B] pt-16 pb-8 border-t border-white/5">
        <div className="site-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {Object.entries(footerLinks).map(([cat, links]) => (
              <div key={cat}>
                <h4 className="text-white font-semibold text-sm mb-4">{cat}</h4>
                <ul className="space-y-2">
                  {links.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="text-[#8D6B7B] text-sm hover:text-white transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="border-t border-white/5 pt-8 mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <span className="text-2xl font-bold text-white">
                Apex<span className="text-[#E11D48]">CFD</span>
              </span>
              <div className="flex gap-3">
                {/* Social icons — colored circles like Plus500 */}
                {[
                  {
                    color: '#1877F2',
                    path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
                  },
                  {
                    color: '#1DA1F2',
                    path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
                  },
                  {
                    color: '#E4405F',
                    path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
                  },
                  {
                    color: '#0A66C2',
                    path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
                  },
                  {
                    color: '#FF0000',
                    path: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
                  },
                  {
                    color: '#0088CC',
                    path: 'M11.944 0A12 12 0 000 12a12 12 0 0012 24 12 12 0 0012-12A12.02 12.02 0 0012 0h-.056zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z',
                  },
                ].map((s, i) => (
                  <a
                    key={i}
                    href="#"
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
                    style={{ backgroundColor: s.color }}
                  >
                    <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
                      <path d={s.path} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 pt-6">
            <p className="text-[11px] text-[#684A58] leading-relaxed mb-4">
              <strong className="text-[#8D6B7B]">Risk Warning:</strong> Trading Forex and CFDs
              involves significant risk and can result in the loss of your invested capital. You
              should not invest more than you can afford to lose and should ensure that you fully
              understand the risks involved. Trading leveraged products may not be suitable for all
              investors.{' '}
              <strong className="text-[#8D6B7B]">
                80% of retail investor accounts lose money when trading CFDs with this provider.
              </strong>{' '}
              Past performance is not indicative of future results.
            </p>
            <p className="text-[10px] text-[#5A3D4F] mt-4">
              &copy; 2026 ApexCFD. All rights reserved. Secured by SSL.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
